/** @jsx jsx */
import React, { useRef, useCallback, useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import throttle from 'lodash/throttle';
import { WidthObserver } from '@atlaskit/width-detector';
import { ConsentBanner } from '@atlassian/browser-storage-controls';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import { MIN_BANNER_WIDTH } from '../../constants.tsx';
import {
	useCookieConsentBannerSettings,
	useCookieConsentBannerSettingsActions,
} from '../../services/preferences/index.tsx';

export const CookieConsentBanner = () => {
	const [{ height }] = useCookieConsentBannerSettings();
	const { onDismiss, setHeight } = useCookieConsentBannerSettingsActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const ref = useRef<HTMLDivElement>(null);

	// banner height is set either due to screen width changes or consent banner finishes rendering
	const setBannerHeight = () => {
		ref.current && setHeight(ref.current.clientHeight);
	};

	// throttled banner height setter is used for dynamic banner height when screen width changes
	const throttledSetBannerHeight = throttle(setBannerHeight, 50);

	useEffect(() => {
		const action = 'rendered';
		const actionSubject = 'cookieConsentBanner';
		const event = createAnalyticsEvent({
			action,
			actionSubject,
		});
		fireUIAnalytics(event, `${actionSubject} ${action}`);
	}, [createAnalyticsEvent]);

	const onConsentBannerBailOut = useCallback(() => {
		const actionSubject = 'consentBanner';
		const action = 'bailedOut';
		const event = createAnalyticsEvent({
			action,
			actionSubject,
		});
		fireTrackAnalytics(event, `${actionSubject} ${action}`);

		// consider banner as dismissed if it reports a bail out
		onDismiss();
	}, [onDismiss, createAnalyticsEvent]);

	return (
		<>
			<WidthObserver setWidth={throttledSetBannerHeight} />
			<div
				ref={ref}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				css={css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					...(!height && {
						visibility: 'hidden',
					}),
				})}
			>
				<ConsentBanner
					onHide={onDismiss}
					onShow={setBannerHeight}
					minWidth={MIN_BANNER_WIDTH}
					onConsentBannerBailOut={onConsentBannerBailOut}
					product="Jira"
				/>
			</div>
		</>
	);
};
